/**
 * Investigator runs the browser is still waiting on.
 *
 * WHAT WAS BROKEN. "Run it for me" launched a real background job and then
 * polled it from component-local `useState`. Navigate from the
 * drug page to search and that component unmounts, the state dies, and the poll
 * stops. The job keeps running on the server, finishes, and nothing ever
 * says so or attaches it — the user is left waiting on a notification that
 * can no longer arrive. There was no toast, no badge, no sound either: the
 * only signal was a chip quietly changing above a composer you had to be
 * looking at.
 *
 * So the watch list moved out of the component and into storage, and the
 * polling moved above the router.
 *
 * WHY sessionStorage AND NOT localStorage. A watched job belongs to this
 * browsing session. In localStorage a job launched last Tuesday would be
 * picked up on a fresh visit and attached to a conversation that never asked
 * for it — a wrong-scope attachment that the user has no memory of causing.
 * A closed tab losing the watch is the honest trade, and the run itself is
 * never lost: it is still a completed job on the server.
 *
 * WHY THE THREAD ID TRAVELS WITH THE JOB. The result attaches to the
 * conversation that ASKED for it, not to whichever conversation happens to be
 * open when it lands. Those are different threads the moment the user moves
 * to another drug, and attaching to the second one would scope a conversation
 * to rows nobody requested there.
 *
 * Pure: no React, no network. The provider drives it.
 */

const STORAGE_KEY = 'cepi-watched-jobs';

/** Beyond this a watch is abandoned rather than polled forever. */
export const MAX_WATCH_MS = 6 * 60 * 60 * 1000;
/** A runaway must not grow without bound; oldest go first. */
export const MAX_WATCHED = 12;

export interface WatchedJob {
  jobId: string;
  /** The conversation that asked. May be null if the thread was not yet born. */
  threadId: string | null;
  /** Shown in the notification, so the user knows WHICH run finished. */
  criterion: string;
  startedAt: number;
}

/**
 * What a run is doing right now.
 *
 * The whole job, not its status word. `fetchJobStatus` used to collapse this
 * payload to `body.job.status` and drop the rest, so the browser fetched
 * "Found 299 candidate drugs. Starting evaluation..." every five seconds and
 * threw it away, leaving the card on `Starting…` for the life of the run
 * (prod, 2026-09-21). Keeping the object is the fix; everything below is
 * presentation on top of it.
 */
export interface JobProgress {
  status: string;
  /** Phase marker, 0-100. Deliberately NOT rendered — see `progressLine`. */
  pct: number;
  /** The backend's own human string. Rendered verbatim. */
  message: string | null;
  /** Matches so far. */
  found: number;
  analyzed: number;
  /** Candidate pool. NOT a denominator — see `progressLine`. */
  total: number;
  /** The cap on MATCHES, which is what the run actually stops at. */
  maxResults: number;
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * A `GET /v2/research/{id}` body -> `JobProgress`, or null if it is not one.
 *
 * Tolerant by design: a missing counter yields 0 and a missing message yields
 * null rather than throwing. A progress display that can crash is worse than
 * the stuck label it replaces, and this parses a response shape that has
 * already changed once (the `{job:{…}}` envelope).
 */
export function parseJobProgress(body: unknown): JobProgress | null {
  if (!body || typeof body !== 'object') return null;
  const outer = body as Record<string, unknown>;
  const nested = outer['job'];
  const inner = (nested && typeof nested === 'object'
    ? nested : outer) as Record<string, unknown>;
  const status = inner['status'];
  if (typeof status !== 'string') return null;
  const message = inner['progress_message'];
  return {
    status,
    pct: num(inner['progress_pct']),
    message: typeof message === 'string' ? message : null,
    found: num(inner['drugs_found']),
    analyzed: num(inner['drugs_analyzed']),
    total: num(inner['drugs_total']),
    maxResults: num(inner['max_results']),
  };
}

/** Said when the run exists but has not reported a phase yet. */
const STATUS_WORD: Record<string, string> = {
  pending: 'Queued…',
  running: 'Running…',
  analyzing: 'Analyzing…',
  resuming: 'Resuming…',
};

/**
 * One line describing a live run.
 *
 * THE BACKEND'S MESSAGE IS THE LINE. `_update_progress` is called from ten
 * sites in `research_service.py` and already writes for a human ("Extracting
 * timeline for KEYTRUDA (3/12)…"). Rendering it verbatim means a phase added
 * to the pipeline next month appears here with no frontend release — a label
 * table in this file would need editing every time.
 *
 * TWO NUMBERS ARE DELIBERATELY ABSENT.
 *   `pct`   is hard-coded per phase (10, 12, 15, 10+70*i/n, 85). As a figure
 *           it lies: it jumps 15 -> 80 and then sits still.
 *   `total` is the candidate POOL (299 on the reported run), not the target.
 *           `research_service.py:233` — "No cap on drugs to evaluate — the
 *           loop stops when max_results matches are found" — so the only
 *           denominator the run is actually driving toward is `maxResults`.
 */
export function progressLine(p: JobProgress): string {
  const base = p.message || STATUS_WORD[p.status] || 'Working…';
  // Zero matches in the first minute is the normal state, not information;
  // and a cap of 0 means an old payload, not "none allowed".
  if (p.found > 0 && p.maxResults > 0) {
    return `${base} · ${p.found} of ${p.maxResults} matched`;
  }
  return base;
}

export type JobOutcome = 'complete' | 'failed' | 'cancelled';

export interface FinishedJob extends WatchedJob {
  outcome: JobOutcome;
  /** Matches, when the run reported them. */
  matched?: number | undefined;
}

/**
 * Statuses that mean "stop polling".
 *
 * `failed` and `cancelled` are reported to the user, not swallowed. A run that
 * dies in silence looks exactly like one still working, and the user waits
 * forever for a notification that is never coming — the failure this module
 * exists to remove, in a different costume.
 */
const RUNNING = new Set(['pending', 'running', 'analyzing', 'resuming']);

export function isRunning(status: string | null): boolean {
  return status !== null && RUNNING.has(status);
}

export function outcomeOf(status: string | null): JobOutcome | null {
  if (status === null || isRunning(status)) return null;
  if (status === 'complete') return 'complete';
  if (status === 'cancelled') return 'cancelled';
  return 'failed';
}

export function loadWatched(now: number): WatchedJob[] {
  let raw: string | null = null;
  try { raw = sessionStorage.getItem(STORAGE_KEY); } catch { return []; }
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((j): j is WatchedJob =>
        !!j && typeof j === 'object'
        && typeof (j as WatchedJob).jobId === 'string'
        && typeof (j as WatchedJob).startedAt === 'number')
      .filter((j) => now - j.startedAt < MAX_WATCH_MS)
      .slice(-MAX_WATCHED);
  } catch {
    return [];
  }
}

export function saveWatched(jobs: WatchedJob[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(jobs.slice(-MAX_WATCHED)));
  } catch {
    /* private mode, quota — the watch degrades to this tab only. */
  }
}

/** Add a job, or refresh the one already watched under that id. */
export function addWatched(
  jobs: WatchedJob[], job: Omit<WatchedJob, 'startedAt'>, now: number,
): WatchedJob[] {
  const next = jobs.filter((j) => j.jobId !== job.jobId);
  next.push({ ...job, startedAt: now });
  return next.slice(-MAX_WATCHED);
}

export function removeWatched(jobs: WatchedJob[], jobId: string): WatchedJob[] {
  return jobs.filter((j) => j.jobId !== jobId);
}

/** A short, honest line for the notification. */
export function outcomeLine(job: FinishedJob): string {
  if (job.outcome === 'cancelled') return 'Run cancelled';
  if (job.outcome === 'failed') return 'Run failed';
  if (typeof job.matched === 'number') {
    return `${job.matched} matched`;
  }
  return 'Run complete';
}

export interface RunProgressView {
  /** null = indeterminate. */
  fraction: number | null;
  figure: string | null;
  line: string;
}

/**
 * One view of a live run for the tab pill and the capability card. Never reads
 * `pct` (a per-phase marker, not a figure).
 */
export function runProgressView(
  p: JobProgress | null, pinned: boolean, fallbackTotal = 0,
): RunProgressView {
  if (p === null) return { fraction: null, figure: null, line: 'Starting…' };
  const matches = `${p.found} ${p.found === 1 ? 'match' : 'matches'} so far`;
  if (pinned) {
    const n = p.total > 0 ? p.total : fallbackTotal;
    if (n > 0) {
      return {
        fraction: Math.min(p.analyzed / n, 1),
        figure: `${p.analyzed}/${n}`,
        line: `${p.analyzed} of ${n} products checked · ${matches}`,
      };
    }
    return { fraction: null, figure: null, line: matches };
  }
  return {
    fraction: null,
    figure: p.maxResults > 0 ? `${p.found}/${p.maxResults}` : `${p.found}`,
    line: p.maxResults > 0
      ? `${p.found} of ${p.maxResults} matched` : `${p.found} matched`,
  };
}

export type CapabilityRunPhase = 'starting' | 'running' | 'stopped' | 'failed' | 'done';

/** R6.1 / R7.3a: an observed watcher outcome beats a local Stopped. */
export function capabilityRunPhase(o: {
  jobId: string | null;
  outcome: JobOutcome | undefined;
  stopped: boolean;
  progress: JobProgress | null | undefined;
}): CapabilityRunPhase {
  if (!o.jobId) return 'starting';
  if (o.outcome === 'complete') return 'done';
  if (o.outcome === 'failed') return 'failed';
  if (o.outcome === 'cancelled') return 'stopped';
  if (o.stopped) return 'stopped';
  if (o.progress) return 'running';
  return 'starting';
}
